import { postalCodesAPI } from 'api';
import { funnel } from 'data';
import { isEmpty } from 'lodash';
import {
  Markdown,
  Card,
  Row,
  Text as TextComponent,
  TextLink,
  Heading,
  Flex,
} from 'mymoria-ui/components';
import { styled, mq, css } from 'mymoria-ui/utils';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Review } from 'types';
import { ReviewObject } from 'types/api';
import { defaultReviewObject, sprintf } from 'utils';
import Rating from './Rating';

const Wrapper = styled.div(
  ({ theme: { radii, space } }) => css`
    background: #e8eeee;
    border-radius: ${radii[1]}px;

    ${mq({ padding: [space[2], space[5]] })};
  `,
);

const StyledMarkdown = styled(Markdown)(
  ({ theme: { space } }) => css`
    ul {
      margin: 0;
      li {
        list-style: none;
        line-height: 1.2;
        margin-bottom: ${space[2]};
        padding-left: ${space[5]};
      }
    }
  `,
);

const RatingSection = styled.div(
  ({ theme: { space } }) => css`
    display: flex;
    align-items: center;
    margin-top: ${space[5]};
  `,
);

const StyledCard = styled(Card)(
  ({
    theme: {
      space: { small },
    },
  }) => css`
    margin-bottom: auto;

    && {
      padding: ${small};
    }
  `,
);

const StyledTextLink = styled(TextLink)(({ theme: { space } }) => ({
  marginLeft: space.small,
}));

const ReviewSection: FunctionComponent<Review> = ({
  Headline,
  SubHeadline,
  Content,
  GoogleReviewUrl,
  GooglePlaceId,
}) => {
  const [ratingObject, setRatingObject] = useState<ReviewObject>(defaultReviewObject);

  useEffect(() => {
    const fetchData = async () => {
      const results = await postalCodesAPI.fetchRatingByPlaceId(GooglePlaceId);
      setRatingObject(results);
    };

    fetchData();
  }, [GooglePlaceId]);

  return (
    <Wrapper>
      <Heading.Heading2 width={[1, '80%', '60%']} mb="small">
        {Headline}
      </Heading.Heading2>
      {SubHeadline && <StyledMarkdown width={[1, '80%', '60%']} children={SubHeadline} />}
      <RatingSection>
        <img src="/img/partners/google.png" alt="google logo" width={150} height={50} />
        <StyledTextLink href={GoogleReviewUrl} target="_blank">
          {sprintf(funnel.intro.review.ratingText, {
            rate: ratingObject.rating,
            reviewCount: ratingObject.user_ratings_total,
          })}
        </StyledTextLink>
      </RatingSection>
      <Row mt="large" flexWrap="wrap">
        {!isEmpty(Content) &&
          Content.slice(0, 3).map(({ value: { Date, PersonName, Text } }, index) => (
            <Flex
              width={[1, null, 1 / 3]}
              key={PersonName}
              px={['unset', null, index === 1 ? 1 : 'unset']}
              py={[index === 1 ? 1 : 'unset', null, 'unset']}
            >
              <StyledCard>
                <Rating rate={5} />
                <TextComponent my="small" fontWeight="bold" display="inline-block">
                  {`${PersonName},`}
                </TextComponent>
                <TextComponent ml="0.5rem" display="inline-block">
                  {Date}
                </TextComponent>
                <Markdown
                  fontSize="1.25rem"
                  fontWeight="normal"
                  children={`"${Text}"`}
                  mb="small"
                />
              </StyledCard>
            </Flex>
          ))}
      </Row>
    </Wrapper>
  );
};

export default ReviewSection;
